// Quick-add button on meal cards inside the native app. Click flow:
//   - If pickup day + store are saved in localStorage, POST /cart/add.js directly.
//   - Otherwise, open the pickup-day modal first and then add the item to the cart.
// Either way, dispatch `native-app:added-to-cart` so the toast snippet picks up.
// On Our Meals: after add, bump cached meal count and re-paint card prices in place
// (no full reload) so scroll position is preserved.
//
// Pricing via window.MealPricingEstimate (assets/meal-pricing-estimate.js).
// Meal cart cap (MAX_MEALS_IN_CART): disable + when cart already has that many meals.

(function () {
  let cartMealCount = 0;

  function isOurMealsPage() {
    return /\/collections\/our-meals\/?/.test(window.location.pathname || '');
  }

  function maxMealsInCart() {
    if (window.MealPricingEstimate && window.MealPricingEstimate.MAX_MEALS_IN_CART != null) {
      return window.MealPricingEstimate.MAX_MEALS_IN_CART;
    }
    return 16;
  }

  function isMealCartAtLimit() {
    if (window.MealPricingEstimate) {
      return window.MealPricingEstimate.isMealCartAtLimit(cartMealCount);
    }
    return cartMealCount >= maxMealsInCart();
  }

  function formatMoney(cents) {
    if (window.MealPricingEstimate) return window.MealPricingEstimate.formatMoney(cents);
    const dollars = cents / 100;
    return Number.isInteger(dollars) ? '$' + dollars : '$' + dollars.toFixed(2);
  }

  function persistMealCount(count) {
    cartMealCount = Math.max(0, Math.floor(Number(count)) || 0);
    try {
      sessionStorage.setItem('gkCartMealCount', String(cartMealCount));
    } catch (e) {}
  }

  function seedCartMealCountFromDom() {
    const card = document.querySelector('.product-item--native-app[data-cart-meal-count]');
    persistMealCount(card ? card.dataset.cartMealCount : 0);
  }

  function pageMealProductIds() {
    const ids = new Set();
    document.querySelectorAll('.product-item--native-app[data-product-id]').forEach(function (el) {
      const id = Number(el.dataset.productId);
      if (id) ids.add(id);
    });
    // Fallback for cards that only expose id="product-item-123"
    document.querySelectorAll('.product-item--native-app[id^="product-item-"]').forEach(function (el) {
      const id = Number(el.id.replace('product-item-', ''));
      if (id) ids.add(id);
    });
    return ids;
  }

  function pageMealVariantIds() {
    const ids = new Set();
    document
      .querySelectorAll('.product-item--native-app .product-item__native-add[data-variant-id]')
      .forEach(function (el) {
        const id = Number(el.dataset.variantId);
        if (id) ids.add(id);
      });
    return ids;
  }

  // Count our-meals line quantities from /cart.js (product/variant ids on this page only).
  function countOurMealsInCart(cart) {
    const productIds = pageMealProductIds();
    const variantIds = pageMealVariantIds();
    return (cart.items || []).reduce(function (sum, item) {
      const productId = Number(item.product_id);
      const variantId = Number(item.variant_id || item.id);
      if (productIds.has(productId) || variantIds.has(variantId)) {
        return sum + (Number(item.quantity) || 0);
      }
      return sum;
    }, 0);
  }

  function ensureListPrice(priceRoot) {
    let listEl = priceRoot.querySelector('.product-item__price-list');
    if (listEl) return listEl;
    listEl = document.createElement('s');
    listEl.className = 'product-item__price-list';
    const atcEl = priceRoot.querySelector('.product-item__price-atc');
    if (atcEl && atcEl.parentNode) {
      // Insert beside ATC inside .product-item__price-amounts (not under .product-item__price).
      atcEl.parentNode.insertBefore(listEl, atcEl);
    } else {
      const amountsEl = priceRoot.querySelector('.product-item__price-amounts');
      if (amountsEl) {
        amountsEl.insertBefore(listEl, amountsEl.firstChild);
      } else {
        priceRoot.insertBefore(listEl, priceRoot.firstChild);
      }
    }
    return listEl;
  }

  function syncMealAddButtons() {
    const atLimit = isMealCartAtLimit();
    document
      .querySelectorAll('.product-item--native-app[data-cart-meal-count] .product-item__native-add')
      .forEach(function (button) {
        button.disabled = atLimit;
        if (atLimit) {
          button.setAttribute('aria-disabled', 'true');
          button.title = 'Cart already has the maximum of ' + maxMealsInCart() + ' meals';
        } else {
          button.removeAttribute('aria-disabled');
          button.removeAttribute('title');
        }
      });
  }

  function paintCard(card, mealCount) {
    // Never overwrite Liquid SSR with a list-price fallback — that causes a flash of
    // full ATC while the savings row (still from SSR) can remain visible in WebView.
    if (!window.MealPricingEstimate) return;

    const unitCents = Number(card.dataset.unitCents) || 0;
    const priceRoot = card.querySelector('.product-item__price');
    const atcEl = card.querySelector('.product-item__price-atc');
    if (!priceRoot || !atcEl) return;

    const pricing = window.MealPricingEstimate.estimate(1, {
      unitCents: unitCents,
      cartMealCount: mealCount,
    });

    atcEl.textContent = formatMoney(pricing.totalCents);
    atcEl.classList.toggle('is-discounted', pricing.discountCents > 0);
    card.dataset.cartMealCount = String(mealCount);

    if (pricing.discountCents > 0) {
      const listEl = ensureListPrice(priceRoot);
      listEl.textContent = formatMoney(pricing.listCents);
      listEl.hidden = false;
    } else {
      const listEl = priceRoot.querySelector('.product-item__price-list');
      if (listEl) {
        listEl.textContent = '';
        listEl.hidden = true;
      }
    }
  }

  function paintAllCards(mealCount) {
    document.querySelectorAll('.product-item--native-app[data-cart-meal-count]').forEach(function (card) {
      paintCard(card, mealCount);
    });
    syncMealAddButtons();
  }

  function reconcileMealCountFromCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        const counted = countOurMealsInCart(cart);
        // Prefer the higher of local bump vs cart match so filtered grids don't undercount
        // meals already in cart that aren't on the current page.
        if (counted > cartMealCount) {
          persistMealCount(counted);
          paintAllCards(cartMealCount);
        } else {
          syncMealAddButtons();
        }
      })
      .catch(function () {
        syncMealAddButtons();
      });
  }

  function afterMealAdded(qtyAdded) {
    persistMealCount(cartMealCount + (Number(qtyAdded) || 1));
    paintAllCards(cartMealCount);
    reconcileMealCountFromCart();
  }

  function buildItems(button) {
    return [
      {
        id: Number(button.dataset.variantId),
        quantity: 1,
      },
    ];
  }

  function directAdd(button, items) {
    const mealCard = button.closest('.product-item--native-app[data-cart-meal-count]');

    //Don't add meals if cart is at limit
    if (button.disabled || (mealCard && isMealCartAtLimit())) return;
    button.setAttribute('aria-busy', 'true');

    //If on the cart page, use cart page add logic
    if (document.body.classList.contains('template-cart') && window.CartState) {
      window.CartState.addToCart({ items })
        .then(function (result) {
          if (result && result.status >= 400) {
            alert(result.description || result.message);
            return;
          }
          window.Cart.notifyToast();
        })
        .catch(function (error) {
          console.error(error);
        })
        .finally(function () {
          button.removeAttribute('aria-busy');
          if (mealCard) syncMealAddButtons();
        });
    }
    //Use Our Meals add logic
    else {
      const qtyAdded = items.reduce(function (sum, item) {
        return sum + (Number(item.quantity) || 0);
      }, 0);
      window.Cart.add(items, {
        addOptions: window.Cart.AddOptions.NATIVE_NOTIFY,
        onAdded: function () {
          if (mealCard && isOurMealsPage()) {
            afterMealAdded(qtyAdded);
          }
        },
      }).finally(function () {
        button.removeAttribute('aria-busy');
        if (mealCard) syncMealAddButtons();
      });
    }
  }

  function onClick(event) {
    const button = event.target.closest('.product-item__native-add');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();

    const items = buildItems(button);

    if (window.Cart.hasPickupSelected()) {
      directAdd(button, items);
      return;
    }

    if (window.PickupModals) {
      window.PickupModals.openNeededPickupModals({
        onDone: function () {
          directAdd(button, items);
        },
      });
    }
  }

  function init() {
    seedCartMealCountFromDom();
    syncMealAddButtons();
    document.addEventListener('click', onClick);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
